var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, literal } from 'lit/static-html.js';
import { FormControlController, validValidityState, } from '../../internal/controllers/form.js';
import { HasSlotController } from '../../internal/controllers/slot.js';
import { SgdfElement } from '../../internal/sgdf-element.js';
import { watch } from '../../internal/watch.js';
import styles from './button.styles.js';
/**
 * @status in-work
 *
 *
 */
let SgdfButton = class SgdfButton extends SgdfElement {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            form: input => {
                // Buttons support a form attribute that points to an arbitrary form, so if this attribute is set we need to query
                // the form from the same root using its id
                if (input.hasAttribute('form')) {
                    const doc = input.getRootNode();
                    const formId = input.getAttribute('form');
                    return doc.getElementById(formId);
                }
                // Fall back to the closest containing form
                return input.closest('form');
            },
            assumeInteractionOn: ['click'],
        });
        this.hasSlotController = new HasSlotController(this, '[default]', 'prefix', 'suffix');
        this.hasFocus = false;
        this.primary = false;
        this.success = false;
        this.neutral = false;
        this.warning = false;
        this.danger = false;
        /** Draws a small button. */
        this.small = false;
        /** Draws an outlined button. */
        this.outlined = false;
        /** Disables the button. */
        this.disabled = false;
        /** Draws the button in a loading state. */
        this.loading = false;
        /** Draws an text button. */
        this.text = false;
        /** Indicates if activating the button should submit the form. */
        this.submit = false;
        /**
         * The name of the button, submitted as a name/value pair with form data, but only when this button is the submitter.
         * This attribute is ignored when `href` is present.
         */
        this.name = '';
        /**
         * The value of the button, submitted as a pair with the button's name as part of the form data, but only when this
         * button is the submitter. This attribute is ignored when `href` is present.
         */
        this.value = '';
        /** When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`. */
        this.href = '';
        /**
         * When using `href`, this attribute will map to the underlying link's `rel` attribute. Unlike regular links, the
         * default is `noreferrer noopener` to prevent security exploits. However, if you're using `target` to point to a
         * specific tab/window, this will prevent that from working correctly. You can remove or change the default value by
         * setting the attribute to an empty string or a value of your choice, respectively.
         */
        this.rel = 'noreferrer noopener';
    }
    /** Gets the validity state object */
    get validity() {
        if (this.isButton()) {
            return this.button.validity;
        }
        return validValidityState;
    }
    /** Gets the validation message */
    get validationMessage() {
        if (this.isButton()) {
            return this.button.validationMessage;
        }
        return '';
    }
    firstUpdated() {
        if (this.isButton()) {
            this.formControlController.updateValidity();
        }
    }
    handleBlur() {
        this.hasFocus = false;
        this.emit('sgdf-blur');
    }
    handleFocus() {
        this.hasFocus = true;
        this.emit('sgdf-focus');
    }
    handleClick() {
        if (this.submit) {
            this.formControlController.submit(this);
        }
    }
    handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    isButton() {
        return this.href ? false : true;
    }
    isLink() {
        return this.href ? true : false;
    }
    handleDisabledChange() {
        if (this.isButton()) {
            // Disabled form controls are always valid
            this.formControlController.setValidity(this.disabled);
        }
    }
    /** Simulates a click on the button. */
    click() {
        this.button.click();
    }
    /** Sets focus on the button. */
    focus(options) {
        this.button.focus(options);
    }
    /** Removes focus from the button. */
    blur() {
        this.button.blur();
    }
    /** Checks for validity but does not show a validation message. Returns `true` when valid and `false` when invalid. */
    checkValidity() {
        if (this.isButton()) {
            return this.button.checkValidity();
        }
        return true;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        if (this.isButton()) {
            return this.button.reportValidity();
        }
        return true;
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        if (this.isButton()) {
            this.button.setCustomValidity(message);
            this.formControlController.updateValidity();
        }
    }
    render() {
        const modes = {
            primary: this.primary &&
                !this.success &&
                !this.neutral &&
                !this.warning &&
                !this.danger,
            success: !this.primary &&
                this.success &&
                !this.neutral &&
                !this.warning &&
                !this.danger,
            neutral: !this.primary &&
                !this.success &&
                this.neutral &&
                !this.warning &&
                !this.danger,
            warning: !this.primary &&
                !this.success &&
                !this.neutral &&
                this.warning &&
                !this.danger,
            danger: !this.primary &&
                !this.success &&
                !this.neutral &&
                !this.warning &&
                this.danger,
            loading: this.loading,
            btn: true,
            'btn-text': this.text,
            'btn-small': this.small,
            'btn-disabled': this.disabled,
            'btn--has-focus': this.hasFocus,
            'btn--has-label': this.hasSlotController.test('[default]'),
            'btn--has-prefix': this.hasSlotController.test('prefix'),
            'btn--has-suffix': this.hasSlotController.test('suffix'),
        };
        // simple mode is default when no value or when there are multiple conflicting values
        modes.simple =
            !this.primary &&
                !modes.success &&
                !this.neutral &&
                !modes.warning &&
                !modes.danger;
        // outlined is not default except in simple mode
        modes.outlined = this.outlined || (modes.simple && !this.text);
        const isLink = this.isLink();
        const tag = isLink ? literal `a` : literal `button`;
        // Interpolate the property into our template
        return html `
            <${tag}
                class=${classMap(modes)}
                ?disabled=${this.disabled}
                aria-disabled=${this.disabled ? 'true' : 'false'}
                tabindex=${this.disabled ? '-1' : '0'}
                @blur=${this.handleBlur}
                @focus=${this.handleFocus}
                @click=${this.handleClick}
                type=${ifDefined(this.submit ? 'submit' : 'button')}
                part="base"
                href=${ifDefined(this.href)}
            >
                <span part="prefix" class="btn-prefix">
                    <slot name="prefix"></slot>
                </span>
                <span part="label" class="btn-label">
                    <slot></slot>
                </span>
                <span part="suffix" class="btn-suffix">
                    <slot name="suffix"></slot>
                </span>
                ${this.loading ? html `<sgdf-spinner></sgdf-spinner>` : ''}
            </${tag}>
        `;
    }
};
SgdfButton.styles = styles;
__decorate([
    query('.btn')
], SgdfButton.prototype, "button", void 0);
__decorate([
    state()
], SgdfButton.prototype, "hasFocus", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "primary", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "success", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "neutral", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "warning", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "danger", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "small", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "outlined", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfButton.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean })
], SgdfButton.prototype, "text", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfButton.prototype, "submit", void 0);
__decorate([
    property()
], SgdfButton.prototype, "name", void 0);
__decorate([
    property()
], SgdfButton.prototype, "value", void 0);
__decorate([
    property()
], SgdfButton.prototype, "href", void 0);
__decorate([
    property()
], SgdfButton.prototype, "target", void 0);
__decorate([
    property()
], SgdfButton.prototype, "rel", void 0);
__decorate([
    property()
], SgdfButton.prototype, "download", void 0);
__decorate([
    watch('disabled', { waitUntilFirstUpdate: true })
], SgdfButton.prototype, "handleDisabledChange", null);
SgdfButton = __decorate([
    customElement('sgdf-button')
], SgdfButton);
export default SgdfButton;
//# sourceMappingURL=button.js.map